// Raw IndexedDB implementation — no idb library (Proxy not available in Chrome 47)
import { XtreamCurrentLiveProgram } from './xtreamAPI';
import i18next from 'i18next';

interface EpgEntry {
  fetchedAt: number;
  language: string;
  programs: XtreamCurrentLiveProgram[];
}

const DB_NAME = 'epg-cache';
const DB_VERSION = 1;
const TTL_MS = 4 * 60 * 60 * 1000;
const PAST_TRIM_MS = 60 * 60 * 1000;

let _db: IDBDatabase | null = null;

function getDb(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  return new Promise<IDBDatabase>(function (resolve, reject) {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = function (e) {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      if (!db.objectStoreNames.contains('streams')) db.createObjectStore('streams');
    };
    req.onsuccess = function (e) {
      _db = (e.target as IDBOpenDBRequest).result;
      resolve(_db);
    };
    req.onerror = function () { reject(req.error); };
  });
}

function idbGet<T>(db: IDBDatabase, store: string, key: string): Promise<T | undefined> {
  return new Promise<T | undefined>(function (resolve, reject) {
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).get(key);
    req.onsuccess = function () { resolve(req.result as T | undefined); };
    req.onerror = function () { reject(req.error); };
  });
}

function idbPut(db: IDBDatabase, store: string, value: unknown, key: string): Promise<void> {
  return new Promise<void>(function (resolve, reject) {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).put(value, key);
    req.onsuccess = function () { resolve(); };
    req.onerror = function () { reject(req.error); };
  });
}

function idbClear(db: IDBDatabase, store: string): Promise<void> {
  return new Promise<void>(function (resolve, reject) {
    const tx = db.transaction(store, 'readwrite');
    const req = tx.objectStore(store).clear();
    req.onsuccess = function () { resolve(); };
    req.onerror = function () { reject(req.error); };
  });
}

function idbGetAll(db: IDBDatabase, store: string): Promise<Array<{ key: string; value: EpgEntry }>> {
  return new Promise(function (resolve, reject) {
    const results: Array<{ key: string; value: EpgEntry }> = [];
    const tx = db.transaction(store, 'readonly');
    const req = tx.objectStore(store).openCursor();
    req.onsuccess = function (e) {
      const cursor = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (cursor) {
        results.push({ key: cursor.key as string, value: cursor.value as EpgEntry });
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    req.onerror = function () { reject(req.error); };
  });
}

export async function ensureHostCache(currentHost: string): Promise<void> {
  try {
    const db = await getDb();
    const savedHost = await idbGet<string>(db, 'meta', 'host');
    if (savedHost !== currentHost) {
      await idbClear(db, 'streams');
      await idbPut(db, 'meta', currentHost, 'host');
    }
  } catch {
    // IndexedDB unavailable (private mode on some browsers)
  }
}

export async function getCachedEpg(streamId: string): Promise<XtreamCurrentLiveProgram[] | null> {
  try {
    const db = await getDb();
    const entry = await idbGet<EpgEntry>(db, 'streams', streamId);
    if (!entry) return null;
    if (entry.language !== (i18next.resolvedLanguage || i18next.language)) return null;
    if (Date.now() - entry.fetchedAt > TTL_MS) return null;
    return entry.programs;
  } catch {
    return null;
  }
}

export async function setCachedEpg(
  streamId: string,
  programs: XtreamCurrentLiveProgram[],
): Promise<void> {
  try {
    const db = await getDb();
    const cutoff = Date.now() - PAST_TRIM_MS;
    const trimmed = programs.filter(function (p) { return p.endMs > cutoff; });
    await idbPut(db, 'streams', {
      fetchedAt: Date.now(),
      language: i18next.resolvedLanguage || i18next.language,
      programs: trimmed,
    }, streamId);
  } catch {
    // best-effort
  }
}

export async function loadAllCachedEpg(): Promise<Record<string, XtreamCurrentLiveProgram[]>> {
  const result: Record<string, XtreamCurrentLiveProgram[]> = {};
  try {
    const db = await getDb();
    const now = Date.now();
    const rows = await idbGetAll(db, 'streams');
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.value.language === (i18next.resolvedLanguage || i18next.language) && now - row.value.fetchedAt <= TTL_MS) {
        result[row.key] = row.value.programs;
      }
    }
  } catch {
    // return empty if IndexedDB unavailable
  }
  return result;
}

export async function clearEpgCache(): Promise<void> {
  try {
    const db = await getDb();
    await idbClear(db, 'streams');
    await idbClear(db, 'meta');
  } catch {
    // IndexedDB is best-effort on constrained TV browsers.
  }
}
